import {
  Box, Flex, Grid, GridItem, Image, Input, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, FormControl, FormLabel, Button, Select, Tooltip, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverCloseButton, useDisclosure, SimpleGrid,
  IconButton,
} from "@chakra-ui/react";
import MyDiv from "./company.style";
import { AddIcon, ChevronDownIcon, TimeIcon } from "@chakra-ui/icons";
import { CustomButton, CustomToast, ActionEditIcon, AlertCard } from "../../../../components";
import { useEffect, useRef, useState, useCallback } from "react";
import PlusIcon from "../../../../assets/images/Vector.svg";
import { ICompany, sortOptions, sortMapping } from "./model";
import { useFetch } from "../../../../hooks/useFetch";
import CustomTables from "../../../../components/CustomTable/CustomTables";
import { Column, ILazyParams } from "../../../../components/CustomTable/CustomTables";
import MultiSelectTypeahead from "../../../../components/AutoCompleteInput/MultiSelectTypeahead";
import { useForm } from "react-hook-form";
import CompanyDocumentDrawer from "./CompanyDocumentDrawer";
import SortIcon from "../../../../assets/images/SortIcon.svg";
import { DirectorHistoryModal } from "./directorHistory";

const GEOAPIFY_API_KEY = "e1c50744d5c64149bc15dc3e2ebc165d";

interface ICountryDropdown {
  id: number;
  country_Name: string;
  country_Code: string;
}

type ManualPerson = { id: string; name: string; isManualEntry: true; manualEmail: string; manualPhone: string };
type ManualDirectorState = { active: boolean; name: string; email: string; phone: string };
const emptyManualDirector: ManualDirectorState = { active: false, name: "", email: "", phone: "" };

const ManualDirectorRow = ({
  manualDirector,
  setManualDirector,
  onAdd,
  onCancel,
}: {
  manualDirector: ManualDirectorState;
  setManualDirector: React.Dispatch<React.SetStateAction<ManualDirectorState>>;
  onAdd: () => void;
  onCancel: () => void;
}) => {
  if (!manualDirector.active) return null;
  return (
    <Box mt={2} p={3} bg="gray.50" borderRadius="md" borderWidth="1px">
      <SimpleGrid columns={2} spacing={2} mb={2}>
        <Input
          size="sm"
          placeholder="Director Name"
          value={manualDirector.name}
          onChange={(e) => setManualDirector((p) => ({ ...p, name: e.target.value }))}
        />
        <Input
          size="sm"
          placeholder="Email"
          type="email"
          value={manualDirector.email}
          onChange={(e) => setManualDirector((p) => ({ ...p, email: e.target.value }))}
        />
        <Input
          size="sm"
          placeholder="Phone Number"
          maxLength={10}
          value={manualDirector.phone}
          onChange={(e) => setManualDirector((p) => ({ ...p, phone: e.target.value }))}
        />
      </SimpleGrid>
      <Flex gap={2} justify="flex-end">
        <Button size="xs" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button size="xs" bg="#006838" color="white" _hover={{ bg: "#00532c" }} onClick={onAdd}>Add</Button>
      </Flex>
    </Box>
  );
};

const Company = () => {
  const [employees, setEmployees] = useState<ICompany[]>([]);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCompany, setCurrentCompany] = useState({
    id: 0,
    company: "",
    address: "",
    city: "",
    zipCode: 0,
    code: "",
    country: "",
    state: "",
    logo: "",
    stamp: "",
    status: true
  });
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [logo, setLogo] = useState<File | null>(null);
  const [companyStamp, setcompanyStamp] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [stampPreview, setStampPreview] = useState<string | null>(null);
  const [AlertOpen, setAlertOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);

  const [directorHistoryModal, setDirectorHistoryModal] = useState({ open: false, companyId: 0, companyName: "" });

  // NEW: director-only options, sourced from the Director module
  const [directorOptions, setDirectorOptions] = useState<any[]>([]);
  const [allDirectors, setAllDirectors] = useState<any[]>([]);

  // NEW: manual "Add New Director" inline form state
  const [manualDirector, setManualDirector] = useState<ManualDirectorState>(emptyManualDirector);

  const [statusChangeSource, setStatusChangeSource] = useState<"table" | "drawer" | null>(null);
  const getStatusColor = (status: boolean) => (status ? "#38A169" : "#E53E3E");
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [previewImage, setPreviewImage] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { isOpen: isSortOpen, onOpen: onSortOpen, onClose: onSortClose } = useDisclosure();
  const [selectedSort, setSelectedSort] = useState<string>(sortOptions[0]);

  const [lazyParams, setLazyParams] = useState<ILazyParams>({
    first: 0,
    rows: 20,
    sortField: undefined,
    sortOrder: undefined,
    multiSortMeta: [],
    filters: {},
    globalFilter: '',
  });

  const openImage = (image: string) => { setPreviewImage(image); setImageModalOpen(true); };

  const [countries, setCountries] = useState<ICountryDropdown[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);
  const countryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  type FormValues = {
    directors: any[];
    auditors: any[];
    pfConsultants: any[];
  };

  const { control, setValue, watch, reset } = useForm<FormValues>({
    defaultValues: {
      directors: [],
      auditors: [],
      pfConsultants: [],
    },
  });

  useEffect(() => { loadCompany(lazyParams); }, [lazyParams]);

  const loadCompany = async (params: ILazyParams) => {
    const qs = new URLSearchParams();
    qs.set('first', String(params.first));
    qs.set('rows', String(params.rows));
    if (params.sortField) qs.set('sortField', params.sortField);
    if (params.sortOrder) qs.set('sortOrder', String(params.sortOrder));
    if (params.globalFilter) qs.set('globalFilter', params.globalFilter);
    if (params.filters && Object.keys(params.filters).length) {
      qs.set('filters', JSON.stringify(params.filters));
    }

    const response = await fetchApi<{ data: ICompany[]; totalRecords: number }>(
      `Hrlense_Company?${qs.toString()}`, "GET"
    );

    if (response) {
      setEmployees(response.data);
      setTotalCount(response.totalRecords);
    }
  };

  const handleAddNewCompany = () => {
    setIsEditMode(false);
    setCurrentCompany({
      id: 0,
      company: "",
      address: "",
      city: "",
      zipCode: 0,
      code: "",
      logo: "",
      stamp: "",
      country: "",
      state: "",
      status: true
    });
    reset({
      directors: [],
      auditors: [],
      pfConsultants: [],
    });
    setLogo(null);
    setcompanyStamp(null);
    setSelectedCountryId(null);
    setCountries([]);
    setCountrySearch("");
    setManualDirector(emptyManualDirector);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (stampInputRef.current) stampInputRef.current.value = "";
    setDrawerOpen(true);
  };

  const handleEditCompany = async (company: ICompany) => {
    setIsEditMode(true);
    setSelectedId(company.id);
    setCurrentCompany(company);
    setValue("directors", company.directors || []);
    setValue("auditors", company.auditors || []);
    setValue("pfConsultants", company.pfConsultants || []);
    setLogoPreview(null);
    setStampPreview(null);
    setManualDirector(emptyManualDirector);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (stampInputRef.current) stampInputRef.current.value = "";

    setSelectedCountryId(null);
    setCountries([]);
    if (company.country) {
      const response = await fetchApi<ICountryDropdown[]>(
        `DropDown/country?filter=${encodeURIComponent(company.country)}`,
        "GET"
      );
      if (response) {
        setCountries(response);
        const match = response.find(
          c => c.country_Name.toLowerCase() === company.country.toLowerCase()
        );
        if (match) setSelectedCountryId(match.id);
      }
    }

    setDrawerOpen(true);
  };

  const handleStatusChange = (id: number, newStatus: boolean) => {
    setSelectedId(id);
    setPendingStatus(newStatus);
    setStatusChangeSource("table");
    setAlertOpen(true);
  };

  const handleDrawerStatusChange = (newStatus: boolean) => {
    setPendingStatus(newStatus);
    setStatusChangeSource("drawer");
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (pendingStatus !== null) {
      const companyId = statusChangeSource === "table" ? selectedId : currentCompany.id;

      const response = await fetchApi(`Hrlense_Company/changestatus/${companyId}`, "PUT", { Id: companyId, Status: pendingStatus });

      if (response) {
        if (statusChangeSource === "table") {
          setEmployees((prev) =>
            prev.map((emp) =>
              emp.id === selectedId ? { ...emp, status: pendingStatus } : emp
            )
          );
        } else {
          setCurrentCompany((prev) => ({ ...prev, status: pendingStatus }));
        }
      }
    }
    setAlertOpen(false);
    loadCompany(lazyParams);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "company" && !/^[A-Za-z\s]*$/.test(value)) {
      return;
    }
    if (name === "city" && !/^[A-Za-z\s]*$/.test(value)) {
      return;
    }
    if (name === "zipCode" && !/^\d*$/.test(value)) {
      return;
    }

    setCurrentCompany(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      setLogo(e.target.files[0]);
    }
  };

  const handleStampChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setcompanyStamp(e.target.files[0]);
    }
  };

  // ---- Manual "Add New Director" handlers (mirrors PayrollCompany) ----
  const openManualDirector = () => setManualDirector({ active: true, name: "", email: "", phone: "" });
  const cancelManualDirector = () => setManualDirector(emptyManualDirector);

  const addManualDirector = () => {
    if (!manualDirector.name.trim()) {
      addToast({ message: "Please enter a director name", status: "warning" });
      return;
    }
    if (manualDirector.phone && !/^\d{10}$/.test(manualDirector.phone)) {
      addToast({ message: "Phone number must be 10 digits", status: "warning" });
      return;
    }

    if (manualDirector.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualDirector.email)) {
      addToast({ message: "Please enter a valid email address", status: "warning" });
      return;
    }

    const newDirector: ManualPerson = {
      id: `manual-${Date.now()}`,
      name: manualDirector.name.trim(),
      isManualEntry: true,
      manualEmail: manualDirector.email.trim(),
      manualPhone: manualDirector.phone.trim(),
    };
    const current = watch("directors") || [];
    setValue("directors", [...current, newDirector] as any);
    cancelManualDirector();
  };

  const splitDirectors = (list: any[]) => {
    const ids = (list || []).filter((x) => !x.isManualEntry).map((x) => x.id);
    const manual = (list || [])
      .filter((x) => x.isManualEntry)
      .map((x) => ({ name: x.name, email: x.manualEmail || "", phone: x.manualPhone || "" }));
    return { ids, manual };
  };
  // -----------------------------------------------------------------------

  const handleSubmit = async () => {
    const directors = watch("directors") || [];
    const auditors = watch("auditors") || [];
    const pfConsultants = watch("pfConsultants") || [];

    const { ids: directorId, manual: manualDirectors } = splitDirectors(directors);
    const auditorId = auditors.map((x: any) => x.id);
    const pfConsultantId = pfConsultants.map((x: any) => x.id);
    if (!currentCompany.company.trim()) {
      addToast({ message: "Please enter the company name", status: "warning" });
      return;
    }
    if (!currentCompany.zipCode || currentCompany.zipCode === 0) {
      addToast({ message: "Please enter the zip code", status: "warning", });
      return;
    }
    if (!currentCompany.address.trim()) {
      addToast({ message: "Please enter the address", status: "warning" });
      return;
    }
    if (!currentCompany.country.trim()) {
      addToast({ message: "Please enter the country", status: "warning" });
      return;
    }
    if (!currentCompany.state.trim()) {
      addToast({ message: "Please enter the state", status: "warning" });
      return;
    }
    if (!currentCompany.city.trim()) {
      addToast({ message: "Please enter the city", status: "warning" });
      return;
    }
    if (!currentCompany.code.trim()) {
      addToast({ message: "Please enter the Company code", status: "warning" });
      return;
    }
    if (isEditMode) {
      const formDataToSend = new FormData();
      formDataToSend.append("id", currentCompany.id.toString());
      formDataToSend.append("company", currentCompany.company);
      formDataToSend.append("address", currentCompany.address);
      formDataToSend.append("city", currentCompany.city);
      formDataToSend.append("country", currentCompany.country);
      formDataToSend.append("state", currentCompany.state);
      formDataToSend.append("code", currentCompany.code);
      formDataToSend.append("zipCode", currentCompany.zipCode.toString());
      formDataToSend.append("status", currentCompany.status.toString());
      directorId.forEach((id: number) => { formDataToSend.append("DirectorId", id.toString()); });
      auditorId.forEach((id: number) => { formDataToSend.append("AuditorId", id.toString()); });
      pfConsultantId.forEach((id: number) => { formDataToSend.append("PfConsultantId", id.toString()); });
      if (manualDirectors.length) formDataToSend.append("ManualDirectors", JSON.stringify(manualDirectors));

      if (logo) formDataToSend.append("logo", logo);
      if (companyStamp) formDataToSend.append("Stamp", companyStamp);
      const response = await fetchApi<any>(`Hrlense_Company/${currentCompany.id}`, "PUT", formDataToSend, null, "Company Updated successfully", true);

      if (response) {
        await loadCompany(lazyParams);
      }
    } else {
      const formDataToSend = new FormData();
      formDataToSend.append("company", currentCompany.company);
      formDataToSend.append("address", currentCompany.address);
      formDataToSend.append("city", currentCompany.city);
      formDataToSend.append("code", currentCompany.code);
      formDataToSend.append("country", currentCompany.country);
      formDataToSend.append("state", currentCompany.state);
      formDataToSend.append("zipCode", currentCompany.zipCode.toString());
      formDataToSend.append("status", currentCompany.status.toString());
      directorId.forEach((id: number) => { formDataToSend.append("DirectorId", id.toString()); });
      auditorId.forEach((id: number) => { formDataToSend.append("AuditorId", id.toString()); });
      pfConsultantId.forEach((id: number) => { formDataToSend.append("PfConsultantId", id.toString()); });
      if (manualDirectors.length) formDataToSend.append("ManualDirectors", JSON.stringify(manualDirectors));
      if (logo) formDataToSend.append("logo", logo);
      if (companyStamp) formDataToSend.append("companyStamp", companyStamp);
      const response = await fetchApi<ICompany>("Hrlense_Company", "POST", formDataToSend, null, "Company added successfully", true);
      if (response) {
        await loadCompany(lazyParams);
      }
    }
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (logo) {
      const url = URL.createObjectURL(logo);
      setLogoPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [logo]);

  useEffect(() => {
    if (companyStamp) {
      const url = URL.createObjectURL(companyStamp);
      setStampPreview(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [companyStamp]);

  const loadEmployeesDropdown = async () => {
    const response = await fetchApi<any[]>("Hrlense_Company/GetEmployeesDropdown", "GET");
    if (response) {
      setEmployeeOptions(response);
      setAllEmployees(response);
    }
  };

  // NEW: load directors from the Director module's endpoint
  const loadDirectorsDropdown = async () => {
    const response = await fetchApi<any[]>("Director/directorsDropdown", "GET");
    if (response) {
      setDirectorOptions(response);
      setAllDirectors(response);
    }
  };

  useEffect(() => {
    loadEmployeesDropdown();
    loadDirectorsDropdown();
  }, []);

  const handleEmployeeSearch = (query?: string) => {
    if (!query) {
      setEmployeeOptions(allEmployees);
      return;
    }

    const filtered = allEmployees.filter((emp) =>
      emp.name.toLowerCase().includes(query.toLowerCase())
    );

    setEmployeeOptions(filtered);
  };

  // NEW: search handler scoped to director-only list
  const handleDirectorSearch = (query?: string) => {
    if (!query) {
      setDirectorOptions(allDirectors);
      return;
    }
    const filtered = allDirectors.filter((d) => d.name.toLowerCase().includes(query.toLowerCase()));
    setDirectorOptions(filtered);
  };

  const handleOpenDocumentMapping = (company: any) => {
    setSelectedCompany(company);
    setDocumentDrawerOpen(true);
  };

  const fetchCountries = useCallback(async (text: string) => {
    const response = await fetchApi<ICountryDropdown[]>(
      text
        ? `DropDown/country?filter=${encodeURIComponent(text)}`
        : `DropDown/country`,
      "GET"
    );

    if (response) {
      setCountries(response);
      setShowCountryDropdown(text.trim().length > 0);
    }
  }, [fetchApi]);

  const handleCountrySearch = (text: string = "") => {
    setCountrySearch(text);

    if (countryDebounceRef.current) {
      clearTimeout(countryDebounceRef.current);
    }

    if (!text.trim()) {
      setShowCountryDropdown(false);
      return;
    }

    countryDebounceRef.current = setTimeout(() => {
      fetchCountries(text);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (countryDebounceRef.current) {
        clearTimeout(countryDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryRef.current &&
        !countryRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleZipCodeBlur = async () => {
    if (!currentCompany.zipCode) return;
    const selectedCountry = countries.find(c => c.id === selectedCountryId);
    if (!selectedCountry) {
      addToast({ message: "Please select a Country before entering Zip Code", status: "warning" });
      return;
    }

    try {
      const res = await fetch(
        `https://api.geoapify.com/v1/geocode/search` +
        `?text=${encodeURIComponent(String(currentCompany.zipCode))}` +
        `&type=postcode` +
        `&filter=countrycode:${selectedCountry.country_Code.toLowerCase()}` +
        `&format=json&apiKey=${GEOAPIFY_API_KEY}`
      );

      const data = await res.json();
      const result = data?.results?.[0];

      if (result) {
        setCurrentCompany(prev => ({
          ...prev,
          country: selectedCountry.country_Name,
          state: result.state || "",
          city: result.city || result.county || "",
        }));
      } else {
        addToast({
          message: `No match found for that postal code in ${selectedCountry.country_Name}. Please enter State/City manually.`,
          status: "warning",
        });
        setCurrentCompany(prev => ({ ...prev, state: "", city: "" }));
      }
    } catch (err) {
      addToast({ message: "Failed to fetch location details", status: "error" });
    }
  };

  const isValidImage = (url?: string | null) => {
    return !!url && url !== "not found" && url !== "null" && url !== "undefined";
  };

  const handleSortSelect = (option: string) => {
    setSelectedSort(option);
    const { field, order } = sortMapping[option];
    setLazyParams(prev => ({ ...prev, sortField: field, sortOrder: order as 1 | -1, first: 0 }));
    onSortClose();
  };

  return (
    <MyDiv>
      <Flex className="page_heading flex_header">
        <Text className="font-poppins text_semibold text_3xl">Company </Text>
        <Flex gap={2}>
          <Popover isOpen={isSortOpen} onOpen={onSortOpen} onClose={onSortClose} placement="bottom-end" closeOnBlur>
            <PopoverTrigger>
              <Image src={SortIcon} alt="Sort" className="icon_btn" cursor="pointer" />
            </PopoverTrigger>
            <PopoverContent w="220px" borderRadius="10px" boxShadow="md">
              <Box px={4} py={2} borderBottom="1px solid #eee">
                <Text className="font-poppins text_sm text_medium" textAlign="center" color="#00000080">Sort By</Text>
                <PopoverCloseButton top="8px" right="12px" />
              </Box>
              <PopoverBody p={0}>
                {sortOptions.map((option) => (
                  <Box key={option} px={4} py={2} cursor="pointer"
                    bg={selectedSort === option ? "#EBEBEB" : "white"}
                    _hover={{ bg: "#EBEBEB" }}
                    onClick={() => handleSortSelect(option)}
                  >
                    <Text className="font-poppins font_dark" fontSize="14px" fontWeight="500">{option}</Text>
                  </Box>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <CustomButton title="Add New Company" className=" btn font-poppins text_sm text_medium" rightIcon={<Image src={PlusIcon} className="logo" />} onClick={handleAddNewCompany} />
        </Flex>
      </Flex>
      <Grid className="grid_container">
        <GridItem colSpan={12}>
          <CustomTables<ICompany>
            value={employees}
            loading={loading}
            emptyMessage="No Companies Found"
            preferenceKey="companyList"
            showGlobalSearch={false}
            showToolbar={true}
            lazy
            lazyParams={lazyParams}
            onLazyParamsChange={setLazyParams}
            totalRecords={totalCount}
            showPagination
            rowsPerPageOptions={[20, 100, 200, 500]}
            maxTableHeight={null}
          >
            <Column header="S. No" body={(_, idx) => lazyParams.first + idx + 1} width={70} />

            <Column
              header="Company"
              field="company"
              sortable
              filterable
              filterType="text"
              frozen="left"
              body={(rowData: any) => (
                <Tooltip label={rowData.company} hasArrow placement="top">
                  <Text maxW="180px" isTruncated cursor="pointer">
                    {rowData.company}
                  </Text>
                </Tooltip>
              )}
            />
            <Column header="Country" field="country" sortable filterable filterType="text" body={(rowData: any) => (<Text>{rowData.country || "N/A"}</Text>)} />
            <Column header="State" field="state" sortable filterable filterType="text" body={(rowData: any) => (<Text>{rowData.state || "N/A"}</Text>)} />
            <Column header="City" field="city" sortable filterable filterType="text" body={(rowData: any) => (<Text>{rowData.city || "N/A"}</Text>)}
            />
            <Column header="Zip Code" field="zipCode" sortable filterable filterType="number" />
            <Column
              header="Address"
              field="address"
              body={(rowData: any) => (
                <Tooltip label={rowData.address} hasArrow placement="top">
                  <Text maxW="8vw" cursor="pointer" isTruncated>
                    {rowData.address}
                  </Text>
                </Tooltip>
              )}
            />
            <Column header="Director Name" field="directors" body={(row: any) => {
              const directors = row.directors?.length > 0 ? row.directors.map((x: any) => x.name).join(", ") : "N/A";
              return (<Tooltip label={directors} hasArrow> <Text isTruncated maxW="8vw"> {directors} </Text> </Tooltip>);
            }}
            />
            <Column header="Auditor Name" field="auditors" body={(row: any) => {
              const auditors = row.auditors?.length > 0 ? row.auditors.map((x: any) => x.name).join(", ") : "N/A";
              return (<Tooltip label={auditors} hasArrow> <Text isTruncated maxW="8vw"> {auditors} </Text></Tooltip>);
            }}
            />

            <Column header="PF Consultant Name" field="pfConsultants" body={(row: any) => {
              const consultants = row.pfConsultants?.length > 0 ? row.pfConsultants.map((x: any) => x.name).join(", ") : "N/A";
              return (<Tooltip label={consultants} hasArrow> <Text isTruncated maxW="8vw"> {consultants} </Text> </Tooltip>);
            }}
            />
            <Column header="Code" field="code" sortable filterable filterType="text" />
            <Column header="Logo" hidden body={(row: any) => row.logo ? (
              <Button size="xs" colorScheme="green" onClick={() => openImage(row.logo)}>View</Button>
            ) : ("-")} />

            <Column header="Stamp" hidden body={(row: any) => row.stamp ? (
              <Button size="xs" colorScheme="green" onClick={() => openImage(row.stamp)}>View</Button>
            ) : ("-")} />
            <Column header="Status" field="status" filterable filterType="boolean" body={(row: any) => (
              <Select value={row.status ? "true" : "false"} onChange={(e) => handleStatusChange(row.id, e.target.value === "true")} width="120px" height="32px" icon={<ChevronDownIcon />} borderRadius="4px" color={getStatusColor(row.status)} className="font-poppins text_medium text_xsm" >
                <option value="true" style={{ color: "#38A169" }}>Active</option>
                <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
              </Select>
            )} />
            <Column header="Actions" frozen="right" width={130} body={(emp: any) => (
              <Flex gap={2} align="center">
                <ActionEditIcon onClick={() => handleEditCompany(emp)} />
                <Box cursor="pointer" bg="#006838" color="white" p="6px" borderRadius="md" title="Assign Documents" onClick={() => handleOpenDocumentMapping(emp)} >
                  <AddIcon boxSize={3} />
                </Box>
                <Tooltip label="Director History" placement="top">
                  <IconButton
                    size="xs"
                    colorScheme="blue"
                    variant="outline"
                    aria-label="Director History"
                    icon={<TimeIcon />}
                    onClick={() => setDirectorHistoryModal({ open: true, companyId: emp.id, companyName: emp.company })} />
                </Tooltip>
              </Flex>
            )} />
          </CustomTables>
        </GridItem>
      </Grid>
      <AlertCard
        isOpen={AlertOpen}
        description="Are you sure you want to change the Company status?"
        onClose={() => setAlertOpen(false)}
        onConfirm={handleConfirmStatusChange}
      />
      <Drawer isOpen={DrawerOpen} placement="right" onClose={() => setDrawerOpen(false)} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">{isEditMode ? "Edit Company" : "Add New Company"}</DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Box py={4}>
              <div className="form-group">
                <Box>
                  <Text fontWeight="bold" mb={2}>Company Logo</Text>
                  <Input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" p={1} />

                  {logoPreview ? (
                    <Image mt={2} src={logoPreview} boxSize="90px" objectFit="contain" border="1px solid #ddd" borderRadius="md" />
                  ) : isEditMode && isValidImage(currentCompany.logo) ? (
                    <Image mt={2} src={currentCompany.logo} boxSize="90px" objectFit="contain" border="1px solid #ddd" borderRadius="md" fallback={<Text fontSize="sm">No Image</Text>}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No Image
                    </Text>
                  )}
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Company Stamp</Text>
                  <Input type="file" ref={stampInputRef} onChange={handleStampChange} accept="image/*" p={1} />

                  {stampPreview ? (
                    <Image mt={2} src={stampPreview} boxSize="90px" objectFit="contain" border="1px solid #ddd" borderRadius="md"
                    />
                  ) : isEditMode && isValidImage(currentCompany.stamp) ? (
                    <Image mt={2} src={currentCompany.stamp} boxSize="90px" objectFit="contain" border="1px solid #ddd" borderRadius="md"
                      fallback={<Text fontSize="sm">No Image</Text>}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No Image
                    </Text>
                  )}
                </Box>
              </div>
              <FormControl mb={4} mt={4} isRequired>
                <FormLabel>Company Name</FormLabel>
                <Input name="company" value={currentCompany.company} onChange={handleInputChange} placeholder="Enter company name" />
              </FormControl>

              <FormControl mb={4} isRequired>
                <FormLabel>Country</FormLabel>

                <Box position="relative" ref={countryRef}>
                  <Input
                    placeholder="Search Country"
                    value={currentCompany.country}
                    onChange={(e) => {
                      setCurrentCompany(prev => ({
                        ...prev,
                        country: e.target.value
                      }));

                      handleCountrySearch(e.target.value);
                    }}
                  />

                  {showCountryDropdown && countries.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid #ddd"
                      borderRadius="6px"
                      mt={1}
                      maxH="220px"
                      overflowY="auto"
                      zIndex={9999}
                      boxShadow="md"
                    >
                      {countries.map(country => (
                        <Box
                          key={country.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: "gray.100" }}
                          onClick={() => {
                            setSelectedCountryId(country.id);

                            setCurrentCompany(prev => ({
                              ...prev,
                              country: country.country_Name,
                              state: "",
                              city: ""
                            }));

                            setShowCountryDropdown(false);
                          }}
                        >
                          {country.country_Name}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </FormControl>

              <FormControl mb={4} isRequired>
                <FormLabel>Zip Code</FormLabel>
                <Input
                  name="zipCode"
                  value={currentCompany.zipCode === 0 ? "" : currentCompany.zipCode}
                  onChange={handleInputChange}
                  onBlur={handleZipCodeBlur}
                  isDisabled={!selectedCountryId}
                  placeholder="Enter Zip Code"
                />
              </FormControl>

              <FormControl mb={4} isRequired>
                <FormLabel>State</FormLabel>
                <Input name="state" value={currentCompany.state} onChange={handleInputChange} placeholder="Auto-filled from Zip Code" />
              </FormControl>

              <FormControl mb={4} isRequired>
                <FormLabel>City</FormLabel>
                <Input name="city" value={currentCompany.city} onChange={handleInputChange} placeholder="Auto-filled from Zip Code" />
              </FormControl>



              <FormControl mb={4} isRequired>
                <FormLabel>Address</FormLabel>
                <Input name="address" value={currentCompany.address} onChange={handleInputChange} placeholder="Enter address" />
              </FormControl >

              <FormControl mb={4} isRequired>
                <FormLabel>Company Code</FormLabel>
                <Input name="code" value={currentCompany.code} onChange={handleInputChange} placeholder="Enter Company Code" />
              </FormControl>

              <FormControl mb={4}>
                <Flex justify="space-between" align="center">
                  <FormLabel mb={0}>Director Name</FormLabel>
                  {!manualDirector.active && (
                    <Button
                      size="xs"
                      variant="link"
                      color="#006838"
                      leftIcon={<AddIcon boxSize={2.5} />}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        (document.activeElement as HTMLElement)?.blur?.();
                        openManualDirector();
                      }}
                    >

                      Add New
                    </Button>
                  )}
                </Flex>
                {/* CHANGED: now sourced from directorOptions / handleDirectorSearch */}
                <MultiSelectTypeahead
                  name="directors" control={control} options={directorOptions} loading={loading}
                  onCompletion={handleDirectorSearch} optionLabel="name" optionValue="id" placeholder="Select Director"
                />
                <ManualDirectorRow
                  manualDirector={manualDirector}
                  setManualDirector={setManualDirector}
                  onAdd={addManualDirector}
                  onCancel={cancelManualDirector}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Auditor Name</FormLabel>
                <MultiSelectTypeahead name="auditors" control={control} options={employeeOptions} loading={loading} onCompletion={handleEmployeeSearch} optionLabel="name" optionValue="id" placeholder="Select Auditor" />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>PF Consultant Name</FormLabel>
                <MultiSelectTypeahead name="pfConsultants" control={control} options={employeeOptions} loading={loading} onCompletion={handleEmployeeSearch} optionLabel="name" optionValue="id" placeholder="Select PF Consultant" />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel>Status</FormLabel>
                <Select w="120px" value={currentCompany.status ? "true" : "false"} onChange={(e) => handleDrawerStatusChange(e.target.value === "true")} color={getStatusColor(currentCompany.status)} >
                  <option value="true" style={{ color: "#38A169" }}>Active</option>
                  <option value="false" style={{ color: "#E53E3E" }}>Inactive</option>
                </Select>
              </FormControl>
            </Box>
            <Flex justify="flex-end" mt={4} gap={2}>
              <Button className="font-poppins text_sm text_medium" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "#D92F34", color: "white" }}>Cancel</Button>
              <Button onClick={handleSubmit} className="font-poppins text_sm text_medium" style={{ backgroundColor: "#006838", color: "white" }}>{isEditMode ? "Update Company" : "Add Company"}</Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={imageModalOpen} onClose={() => setImageModalOpen(false)} size="xl" isCentered >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Image Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Image src={previewImage} alt="Preview" w="100%" maxH="500px" objectFit="contain" fallback={<Text>Image not found</Text>} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <DirectorHistoryModal isOpen={directorHistoryModal.open} onClose={() => setDirectorHistoryModal({ open: false, companyId: 0, companyName: "" })} companyId={directorHistoryModal.companyId} companyName={directorHistoryModal.companyName} />

      <CompanyDocumentDrawer isOpen={documentDrawerOpen} onClose={() => setDocumentDrawerOpen(false)} company={selectedCompany} type="COMPANY" />
    </MyDiv >
  );
};
export default Company;